import { SEED_FREE_MAIL_DOMAINS } from '../screening/engine';
import { emailDomain, websiteDomain } from '../records/normalise';
import { isEmailAddress, validatePhone } from '../validators';
import type { AiFailureReason, ProviderCaller } from './aiSeam';

/**
 * The enrichment seam (piece 2 declares it, piece 21 builds it; decision 5381;
 * T2737). One many-active interface, `EnrichmentProvider`, with three adapters
 * behind it — a licensed people-data provider, the company's own website, and a
 * search through the AI seam's web search tool — and a scripted fake.
 *
 * 🔒 The owner accepted on 2026-10-17 that a person's name and email and a
 * company's name, website or email domain and postal address leave the
 * building. 🔨 Nothing else does: no phone, no EIN, no DUNS number, no note.
 */

/** 🔒 The switch row (decision 1651): the active adapters, or `off`; an absent row reads `off`. */
export const ENRICHMENT_PROVIDER_KEY = 'enrichment.provider';
/** 🔒 The people-data provider's key, sealed in the KeyProvider envelope the way `ai.anthropicApiKey` is. */
export const PEOPLE_DATA_KEY_KEY = 'enrichment.peopleDataApiKey';
/** 🔒 The provider's price per lookup, a whole number of cents; no row is seeded. */
export const PEOPLE_DATA_CENTS_KEY = 'enrichment.peopleDataCentsPerLookup';

/**
 * 🔒 The people-data seam's monthly call ceiling (decision 5460; T2767): a count of
 * the month's lookups the cost log holds for the adapter, whatever each cost. An
 * absent row reads the vendor's free hundred.
 */
export const PEOPLE_DATA_CALL_CAP_KEY = 'providerCalls.peopleDataMonthlyCallCap';
export const DEFAULT_PEOPLE_DATA_CALL_CAP = 100;

/** The three adapters, as `enrichment.provider` names them and the cost log's `adapter` column records them. */
export const ENRICHMENT_ADAPTERS = ['peopleData', 'website', 'search'] as const;
export type EnrichmentAdapterKey = (typeof ENRICHMENT_ADAPTERS)[number];

/** Every `enrichment.*` row, each audited by the one Settings write (decision 5381). */
export const ENRICHMENT_SETTING_KEYS: readonly string[] = [ENRICHMENT_PROVIDER_KEY, PEOPLE_DATA_KEY_KEY, PEOPLE_DATA_CENTS_KEY, PEOPLE_DATA_CALL_CAP_KEY];

/**
 * 🔒 The vendor the people-data adapter speaks to today (decision 5410): People
 * Data Labs, built behind the interface while the owner's choice stays open, so a
 * second vendor is a second adapter and nothing else moves.
 */
export const PEOPLE_DATA_VENDOR = 'peopleDataLabs';
export const PEOPLE_DATA_VENDOR_NAME = 'People Data Labs';
export const PEOPLE_DATA_KEYS_URL = 'https://dashboard.peopledatalabs.com/api-keys';

/** The adapters the switch row holds: a list among the three, or none for `off`, an absent row or anything unreadable. */
export function enrichmentAdaptersOf(value: unknown): EnrichmentAdapterKey[] {
  if (!Array.isArray(value)) return [];
  return ENRICHMENT_ADAPTERS.filter((adapter) => value.includes(adapter));
}

/** The refusal a value of one of the seam's own rows earns, or null; a blank clears the row. */
export function enrichmentSettingProblem(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (key === ENRICHMENT_PROVIDER_KEY) {
    if (value === 'off') return null;
    const listed = Array.isArray(value) && value.every((item) => (ENRICHMENT_ADAPTERS as readonly unknown[]).includes(item)) && new Set(value).size === value.length;
    return listed ? null : `The switch holds a list among ${ENRICHMENT_ADAPTERS.join(', ')}, or off, nothing else.`;
  }
  if (key === PEOPLE_DATA_CENTS_KEY) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 100_000) return 'The price is a whole number of cents a lookup, nought or more.';
  }
  if (key === PEOPLE_DATA_CALL_CAP_KEY) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 1_000_000) return 'The ceiling is a whole number of lookups a month, nought or more.';
  }
  return null;
}

/**
 * 🔨 A person lookup sends the person's name, their email and their company's
 * name. Nothing else (decision 5381).
 */
export const PERSON_LOOKUP_FIELDS = ['name', 'email', 'companyName'] as const;
/**
 * 🔨 A company lookup sends the company's name, its website or an email domain,
 * and its postal address. Nothing else (decision 5381).
 */
export const COMPANY_LOOKUP_FIELDS = ['name', 'website', 'emailDomain', 'postalAddress'] as const;

export type PersonLookupField = (typeof PERSON_LOOKUP_FIELDS)[number];
export type CompanyLookupField = (typeof COMPANY_LOOKUP_FIELDS)[number];

/** 🔒 A person query (piece 2): the name, the email and the company's name, each optional but the name. */
export interface PersonLookup {
  name: string;
  email?: string | null;
  companyName?: string | null;
}

/** 🔒 A company query (piece 2): the name, its website or an email domain, and the postal address on one line. */
export interface CompanyLookup {
  name: string;
  website?: string | null;
  emailDomain?: string | null;
  postalAddress?: string | null;
}

/**
 * The fields a lookup sends, in the list's order, with each blank one left out,
 * or the fields it names outside its list. 🔨 A website wins over an email
 * domain, so a company lookup sends one of the two and never both.
 */
export function lookupFields(kind: 'person' | 'company', query: Readonly<Record<string, unknown>>): { fields: Record<string, string>; outside: string[] } {
  const allowed: readonly string[] = kind === 'person' ? PERSON_LOOKUP_FIELDS : COMPANY_LOOKUP_FIELDS;
  const outside = Object.keys(query).filter((field) => !allowed.includes(field));
  const fields: Record<string, string> = {};
  for (const field of allowed) {
    const value = query[field];
    if (typeof value === 'string' && value.trim() !== '') fields[field] = value.trim();
  }
  if (fields.website && fields.emailDomain) delete fields.emailDomain;
  return { fields, outside };
}

/** 🔨 A free-mail domain is never a company's domain; the list is the screening engine's own (decision 5381). */
export const FREE_MAIL_DOMAINS: readonly string[] = SEED_FREE_MAIL_DOMAINS;

/** 🔨 LinkedIn's hosts, which no adapter fetches, because its terms forbid automated reading (decision 5381). */
export const LINKEDIN_HOSTS: readonly string[] = ['linkedin.com', 'lnkd.in', 'licdn.com'];

/** Whether a host is one of LinkedIn's or under one. */
export function isLinkedInHost(host: string): boolean {
  const bare = host.trim().toLowerCase().replace(/\.$/, '');
  return LINKEDIN_HOSTS.some((root) => bare === root || bare.endsWith(`.${root}`));
}

/**
 * 🔒 The record's own domain the website adapter reads (decision 5381): the
 * website's host without `www`, or the email domain where no website is held.
 * A free-mail domain or a LinkedIn host gives nothing to fetch.
 */
export function recordDomain(query: Pick<CompanyLookup, 'website' | 'emailDomain'>): string | null {
  const domain = (query.website ? websiteDomain(query.website) : null) ?? (query.emailDomain ? (query.emailDomain.includes('@') ? emailDomain(query.emailDomain) : websiteDomain(query.emailDomain)) : null);
  if (!domain || FREE_MAIL_DOMAINS.includes(domain) || isLinkedInHost(domain)) return null;
  return domain;
}

/** Whether a host is the record's own domain or a name under it. */
export function withinDomain(host: string, domain: string): boolean {
  const bare = host.trim().toLowerCase().replace(/\.$/, '');
  return bare === domain || bare.endsWith(`.${domain}`);
}

/**
 * What a person lookup can find, and a company lookup. 🔒 Decision 6180 reads a
 * licensed answer whole: the person's LinkedIn page, each work-history entry,
 * education entry and skill, six assumed values, and the current employer's
 * detail; a company's answer gives the same detail of the company itself.
 */
export const PERSON_FINDING_FIELDS = [
  'fullName', 'title', 'email', 'companyName', 'phone',
  'linkedIn', 'experience', 'education', 'skill',
  'seniority', 'jobRole', 'jobSubRole', 'yearsExperience', 'salaryBand', 'industry',
  'employerWebsite', 'employerAddress', 'employerLinkedIn', 'employerIndustry', 'employerSize', 'employerFounded',
] as const;
export const COMPANY_FINDING_FIELDS = ['name', 'website', 'phone', 'address', 'linkedIn', 'industry', 'size', 'founded'] as const;
export type EnrichmentField = (typeof PERSON_FINDING_FIELDS)[number] | (typeof COMPANY_FINDING_FIELDS)[number];

/** 🔒 The fields an answer may hold several of, one finding per entry (decision 6180). */
export const LIST_FINDING_FIELDS: readonly EnrichmentField[] = ['experience', 'education', 'skill'];

/**
 * 🔒 The parts of one entry (decision 6180): a work-history entry's `employer`,
 * `title`, `start`, `end`, `website`, `linkedIn` and `current`; an education
 * entry's `school`, `degree`, `fieldOfStudy`, `start`, `end` and `linkedIn`; an
 * address's `street`, `suite`, `locality`, `region` and `postalCode`.
 */
export type EnrichmentDetail = Readonly<Record<string, string>>;

/** 🔒 One value found: the field, its value, the adapter that found it, and the address it was read on or the provider's name. */
export interface EnrichmentFinding {
  field: EnrichmentField;
  value: string;
  adapter: EnrichmentAdapterKey;
  source: string;
  /** The parts of a list entry or an address, each passed through its own rule (decision 6180). */
  detail?: EnrichmentDetail;
}

/** Why an adapter answered nothing: the AI seam's own refusals, no match, nothing to read, or a refused address. */
export type EnrichmentRefusal = AiFailureReason | 'capReached' | 'callCeilingReached' | 'fieldNotListed' | 'inactive' | 'noMatch' | 'nothingToRead' | 'addressRefused';

/** 🔒 A value type, not a table (piece 2): every finding, and each asked adapter that found nothing with its reason. */
export interface EnrichmentAnswer {
  findings: EnrichmentFinding[];
  refusals: { adapter: EnrichmentAdapterKey; refusal: EnrichmentRefusal }[];
}

/** 🔒 The seam (piece 2): `lookupPerson`, `lookupCompany` and `adaptersActive`, each lookup naming who asked for the cost log. */
export interface EnrichmentProvider {
  lookupPerson(query: PersonLookup, caller: ProviderCaller): Promise<EnrichmentAnswer>;
  lookupCompany(query: CompanyLookup, caller: ProviderCaller): Promise<EnrichmentAnswer>;
  adaptersActive(): Promise<EnrichmentAdapterKey[]>;
}

/** The longest name, title or address line kept from an answer; anything longer is not a value a record holds. */
const TEXT_MAX = 300;

/**
 * 🔨 Every answer is outside data (decisions 0202, 5381): each value goes
 * through the record field's own rule, and a value that fails it is dropped.
 * An email is one address, a phone a number the phone rule places, a website
 * one with a domain, and a name, title or address plain text of a sane length.
 */
export function validFindingValue(field: EnrichmentField, value: unknown, now: Date = new Date()): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text === '' || text.length > TEXT_MAX || /[<>]/.test(text)) return null;
  if (field === 'email') return isEmailAddress(text) ? text.toLowerCase() : null;
  if (field === 'phone') {
    const outcome = validatePhone(text, 'US');
    return outcome.ok ? outcome.normalized : null;
  }
  if (field === 'website' || field === 'employerWebsite') return websiteDomain(text) && !isLinkedInHost(websiteDomain(text)!) ? text : null;
  if (field === 'linkedIn' || field === 'employerLinkedIn') return linkedInPage(text);
  if (field === 'founded' || field === 'employerFounded') return foundedYearOf(text, now);
  if (field === 'yearsExperience') return /^\d{1,2}$/.test(text) && Number(text) <= 80 ? String(Number(text)) : null;
  return text;
}

/**
 * 🔨 A LinkedIn page is kept only where its host is one of LinkedIn's (decision
 * 6180), written as an HTTPS address, and kept as a link nothing fetches
 * (decision 5381). The vendor writes it without a scheme.
 */
export function linkedInPage(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text === '' || text.length > TEXT_MAX || /[<>\s]/.test(text)) return null;
  const address = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  let url: URL;
  try {
    url = new URL(address);
  } catch {
    // silent: an address that does not parse is not a LinkedIn page, and the value is dropped (decision 0202)
    return null;
  }
  if (!isLinkedInHost(url.hostname) || url.pathname.length <= 1) return null;
  return `https://${url.hostname.toLowerCase()}${url.pathname.replace(/\/+$/, '')}`;
}

/** 🔨 A founding year is four digits from 1600 to the current year (decision 6180). */
export function foundedYearOf(value: string, now: Date = new Date()): string | null {
  const text = value.trim();
  if (!/^\d{4}$/.test(text)) return null;
  const year = Number(text);
  return year >= 1600 && year <= now.getUTCFullYear() ? text : null;
}

/** 🔨 A start or an end is a year, or a year and month, as 2015 or 2015-09; a day the vendor adds is cut, and anything else is dropped (decision 6180). */
export function answerMonthOf(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})(?:-(\d{2})(?:-\d{2})?)?$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  if (year < 1900 || year > 2200) return null;
  if (match[2] === undefined) return match[1]!;
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? `${match[1]}-${match[2]}` : null;
}

/** The parts each list field and each address may carry, and the rule each part keeps. */
const DETAIL_PARTS: Readonly<Partial<Record<EnrichmentField, readonly string[]>>> = {
  experience: ['employer', 'title', 'start', 'end', 'website', 'linkedIn', 'current'],
  education: ['school', 'degree', 'fieldOfStudy', 'start', 'end', 'linkedIn'],
  address: ['street', 'suite', 'locality', 'region', 'postalCode'],
  employerAddress: ['street', 'suite', 'locality', 'region', 'postalCode'],
};

/**
 * 🔨 An entry's parts, each through its own rule, a part failing it dropped
 * (decisions 0202, 6180). A work-history entry with no employer and an
 * education entry with no school are no entry at all.
 */
export function validFindingDetail(field: EnrichmentField, detail: unknown): EnrichmentDetail | null {
  const parts = DETAIL_PARTS[field];
  if (!parts || !detail || typeof detail !== 'object') return null;
  const out: Record<string, string> = {};
  for (const part of parts) {
    const raw = (detail as Record<string, unknown>)[part];
    let value: string | null;
    if (part === 'start' || part === 'end') value = answerMonthOf(raw);
    else if (part === 'linkedIn') value = linkedInPage(raw);
    else if (part === 'website') value = validFindingValue('website', raw);
    else if (part === 'current') value = raw === 'true' ? 'true' : null;
    else value = typeof raw === 'string' ? validFindingValue('fullName', raw) : null;
    if (value !== null) out[part] = value;
  }
  if (field === 'experience' && !out.employer) return null;
  if (field === 'education' && !out.school) return null;
  return Object.keys(out).length === 0 ? null : out;
}

/** 🔒 The card's own address (decision 5381) and its Test Connection. */
export const PEOPLE_DATA_SETUP_ROUTE = '/settings/integrations/people-data';
export const PEOPLE_DATA_USAGE_PATH = '/settings/integrations/people-data';
export const PEOPLE_DATA_TEST_PATH = '/settings/integrations/people-data/test';

/** 🔒 The card's reading of the month's lookups against the ceiling in force (decision 5460). */
export interface PeopleDataUsage {
  month: string;
  calls: number;
  ceiling: number;
  ceilingHeld: boolean;
}

/** 🔒 What Test Connection reads: the provider's answer for the Organization's own company, or the refusal. */
export type PeopleDataCheck = { ok: true; matched: boolean; findings: EnrichmentFinding[] } | { ok: false; refusal: EnrichmentRefusal };
